/**
 * Local design-preview mock chat sessions for Chat sidebar, library, and demo replies.
 * Used in development when NODE_ENV is development, or when
 * REACT_APP_USE_MOCK_CHAT_SESSIONS=true.
 */

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function iso(value) {
  return value;
}

function mockCandidate({
  id,
  name,
  domain,
  city,
  region,
  industry,
  employees,
  revenue,
  description,
}) {
  return {
    company_id: id,
    company_name: name,
    domain,
    industry,
    employee_count: employees,
    headcount: employees,
    metadata: {
      industry,
      description,
      primary_location: { city, region, country: 'US' },
      website: domain,
    },
    company: {
      name,
      domain,
      website: domain,
      industry,
      description,
      employee_count: employees,
      revenue,
    },
    evidence: [description],
  };
}

const HVAC_CANDIDATES = [
  mockCandidate({
    id: 'mock-hvac-1',
    name: 'Apex Mechanical',
    domain: 'apexmechanical.com',
    city: 'Los Angeles',
    region: 'CA',
    industry: 'HVAC services',
    employees: 92,
    revenue: 18000000,
    description: 'Commercial HVAC contractor serving Southern California campuses and offices.',
  }),
  mockCandidate({
    id: 'mock-hvac-2',
    name: 'Pacific Climate Systems',
    domain: 'pacificclimatesystems.com',
    city: 'San Jose',
    region: 'CA',
    industry: 'Building services',
    employees: 140,
    revenue: 31000000,
    description: 'Design-build HVAC and controls firm focused on life-science facilities.',
  }),
  mockCandidate({
    id: 'mock-hvac-3',
    name: 'Harbor Air Group',
    domain: 'harborairgroup.com',
    city: 'San Diego',
    region: 'CA',
    industry: 'HVAC services',
    employees: 67,
    revenue: 12000000,
    description: 'Regional service provider for hospitality and healthcare mechanical systems.',
  }),
  mockCandidate({
    id: 'mock-hvac-4',
    name: 'Sierra Cooling Co.',
    domain: 'sierracooling.co',
    city: 'Sacramento',
    region: 'CA',
    industry: 'Facilities services',
    employees: 54,
    revenue: 9000000,
    description: 'Preventive maintenance and retrofit programs for Central Valley operators.',
  }),
];

const SAAS_CANDIDATES = [
  mockCandidate({
    id: 'mock-saas-1',
    name: 'Northline Analytics',
    domain: 'northline.io',
    city: 'New York',
    region: 'NY',
    industry: 'SaaS',
    employees: 118,
    revenue: 22000000,
    description: 'Revenue operations software for mid-market B2B teams.',
  }),
  mockCandidate({
    id: 'mock-saas-2',
    name: 'Kiteboard',
    domain: 'kiteboard.com',
    city: 'Brooklyn',
    region: 'NY',
    industry: 'SaaS',
    employees: 74,
    revenue: 11000000,
    description: 'Collaborative planning platform used by growth-stage SaaS companies.',
  }),
  mockCandidate({
    id: 'mock-saas-3',
    name: 'Harbor Ledger',
    domain: 'harborledger.com',
    city: 'New York',
    region: 'NY',
    industry: 'Fintech SaaS',
    employees: 160,
    revenue: 28000000,
    description: 'Billing and collections software for professional services firms.',
  }),
];

const CYBER_CANDIDATES = [
  mockCandidate({
    id: 'mock-cyber-1',
    name: 'Nimbus Shield',
    domain: 'nimbusshield.com',
    city: 'Austin',
    region: 'TX',
    industry: 'Cybersecurity',
    employees: 88,
    revenue: 16000000,
    description: 'Managed detection and response for mid-market enterprises.',
  }),
  mockCandidate({
    id: 'mock-cyber-2',
    name: 'Lattice Guard',
    domain: 'latticeguard.io',
    city: 'Denver',
    region: 'CO',
    industry: 'Cybersecurity',
    employees: 132,
    revenue: 24000000,
    description: 'Identity security platform for hybrid workforce environments.',
  }),
  mockCandidate({
    id: 'mock-cyber-3',
    name: 'Packetforge',
    domain: 'packetforge.com',
    city: 'Boston',
    region: 'MA',
    industry: 'Cybersecurity',
    employees: 61,
    revenue: 8000000,
    description: 'Network detection software used by security operations teams.',
  }),
];

function artifactPayload(id, title, candidates) {
  return {
    artifact_id: id,
    artifact: {
      artifact_id: id,
      title,
      stored_candidate_count: candidates.length,
      sample_limit: 5,
      actions: [
        {
          key: 'export_csv',
          label: 'Export',
          kind: 'export',
          enabled: false,
          disabled_reason: 'Demo preview — export is unavailable.',
        },
      ],
      suggestions: [{ key: 'find_similar', prompt: 'Find similar companies to this list.' }],
    },
    candidates,
  };
}

function historyMessage({ id, role, content, createdAt, extra = {} }) {
  return {
    message_id: id,
    role,
    content,
    created_at: createdAt,
    ...extra,
  };
}

const HVAC_ARTIFACT = artifactPayload(
  'mock-artifact-hvac',
  'HVAC companies in California',
  HVAC_CANDIDATES
);
const SAAS_ARTIFACT = artifactPayload(
  'mock-artifact-saas',
  'SaaS companies in New York',
  SAAS_CANDIDATES
);
const CYBER_ARTIFACT = artifactPayload(
  'mock-artifact-cyber',
  'Cybersecurity firms with 50–200 employees',
  CYBER_CANDIDATES
);

const BASE_MOCK_CHAT_SESSIONS = [
  {
    session_id: 'mock-chat-hvac',
    status: 'active',
    workspace_id: null,
    title: 'Find HVAC companies in California',
    last_message_preview: 'Narrow to companies with revenue above $1M.',
    turn_count: 2,
    created_at: iso('2026-08-10T14:00:00Z'),
    updated_at: iso('2026-08-10T14:12:00Z'),
    last_artifact_id: HVAC_ARTIFACT.artifact_id,
    messages: [
      historyMessage({
        id: 'mock-chat-hvac-user-1',
        role: 'user',
        content: 'Find HVAC companies in California',
        createdAt: '2026-08-10T14:00:00Z',
      }),
      historyMessage({
        id: 'mock-chat-hvac-assistant-1',
        role: 'assistant',
        content: 'Found 4 California HVAC operators that match this search. Previewing the saved target list.',
        createdAt: '2026-08-10T14:01:20Z',
        extra: HVAC_ARTIFACT,
      }),
      historyMessage({
        id: 'mock-chat-hvac-user-2',
        role: 'user',
        content: 'Narrow to companies with revenue above $1M.',
        createdAt: '2026-08-10T14:11:00Z',
      }),
      historyMessage({
        id: 'mock-chat-hvac-assistant-2',
        role: 'assistant',
        content:
          'All 4 remain above $1M revenue. Apex Mechanical and Pacific Climate Systems are the strongest commercial-scale fits; Harbor Air Group and Sierra Cooling Co. are smaller regional operators.',
        createdAt: '2026-08-10T14:12:00Z',
      }),
    ],
  },
  {
    session_id: 'mock-chat-saas',
    status: 'active',
    workspace_id: null,
    title: 'SaaS companies in New York',
    last_message_preview: 'Find SaaS companies in New York with less than 200 employees.',
    turn_count: 1,
    created_at: iso('2026-08-08T09:30:00Z'),
    updated_at: iso('2026-08-08T09:45:00Z'),
    last_artifact_id: SAAS_ARTIFACT.artifact_id,
    messages: [
      historyMessage({
        id: 'mock-chat-saas-user-1',
        role: 'user',
        content: 'Find SaaS companies in New York.',
        createdAt: '2026-08-08T09:30:00Z',
      }),
      historyMessage({
        id: 'mock-chat-saas-assistant-1',
        role: 'assistant',
        content: 'Found 3 New York SaaS companies under 200 employees.',
        createdAt: '2026-08-08T09:45:00Z',
        extra: SAAS_ARTIFACT,
      }),
    ],
  },
  {
    session_id: 'mock-chat-healthcare',
    status: 'active',
    workspace_id: null,
    title: 'Fast-growing healthcare operators',
    last_message_preview: 'Show fast-growing healthcare companies in the Midwest.',
    turn_count: 1,
    created_at: iso('2026-08-05T16:20:00Z'),
    updated_at: iso('2026-08-06T11:00:00Z'),
    last_artifact_id: null,
    messages: [
      historyMessage({
        id: 'mock-chat-healthcare-user-1',
        role: 'user',
        content: 'Show fast-growing healthcare companies.',
        createdAt: '2026-08-05T16:20:00Z',
      }),
      historyMessage({
        id: 'mock-chat-healthcare-assistant-1',
        role: 'assistant',
        content: [
          'Midwest healthcare operators are expanding through outpatient clinics and specialty networks rather than hospital M&A.',
          '',
          'What stands out:',
          '• Multi-site primary care groups adding 8–15 locations a year',
          '• Behavioral health platforms hiring 40%+ year over year',
          '• Home-health operators winning hospital-at-home contracts',
          '',
          'Useful background: https://www.cms.gov and https://www.aha.org',
        ].join('\n'),
        createdAt: '2026-08-06T11:00:00Z',
      }),
    ],
  },
  {
    session_id: 'mock-chat-fintech',
    status: 'active',
    workspace_id: null,
    title: 'Upcoming fintech conferences',
    last_message_preview: 'Find upcoming fintech conferences on the East Coast.',
    turn_count: 1,
    created_at: iso('2026-08-01T10:00:00Z'),
    updated_at: iso('2026-08-01T10:05:00Z'),
    last_artifact_id: null,
    messages: [
      historyMessage({
        id: 'mock-chat-fintech-user-1',
        role: 'user',
        content: 'Find upcoming fintech conferences.',
        createdAt: '2026-08-01T10:00:00Z',
      }),
      historyMessage({
        id: 'mock-chat-fintech-assistant-1',
        role: 'assistant',
        content: [
          'Three East Coast events worth watching:',
          '• Money20/20 USA — Las Vegas listing plus NYC satellite briefings: https://www.money2020.com',
          '• FinTech Meetup — New York: https://fintechmeetup.com',
          '• Sibos — financial-market infrastructure track: https://www.sibos.com',
        ].join('\n'),
        createdAt: '2026-08-01T10:05:00Z',
      }),
    ],
  },
  {
    session_id: 'mock-chat-cyber',
    status: 'active',
    workspace_id: null,
    title: 'Cybersecurity firms 50–200 employees',
    last_message_preview: 'Which cybersecurity companies have 50–200 employees?',
    turn_count: 1,
    created_at: iso('2026-07-28T13:15:00Z'),
    updated_at: iso('2026-07-28T13:30:00Z'),
    last_artifact_id: CYBER_ARTIFACT.artifact_id,
    messages: [
      historyMessage({
        id: 'mock-chat-cyber-user-1',
        role: 'user',
        content: 'Which cybersecurity companies have 50–200 employees?',
        createdAt: '2026-07-28T13:15:00Z',
      }),
      historyMessage({
        id: 'mock-chat-cyber-assistant-1',
        role: 'assistant',
        content: 'Found 3 cybersecurity companies in the 50–200 employee range.',
        createdAt: '2026-07-28T13:30:00Z',
        extra: CYBER_ARTIFACT,
      }),
    ],
  },
];

let mockChatSessionStore = clone(BASE_MOCK_CHAT_SESSIONS);
let mockSessionCounter = 0;

export const MOCK_CHAT_SESSIONS = BASE_MOCK_CHAT_SESSIONS;

export const MOCK_CHAT_VOICE_FALLBACK = 'Find HVAC companies in California.';

export const isMockChatSessionsEnabled = () =>
  process.env.REACT_APP_USE_MOCK_CHAT_SESSIONS === 'true';

export function resetMockChatSessionsForTests() {
  mockChatSessionStore = clone(BASE_MOCK_CHAT_SESSIONS);
  mockSessionCounter = 0;
}

function sessionSummary(session) {
  const { messages, ...rest } = session;
  const searchText = (messages || [])
    .map((message) => String(message?.content || '').trim())
    .filter(Boolean)
    .join(' ');
  return {
    ...rest,
    search_text: searchText,
  };
}

export function classifyMockChatPrompt(text) {
  const query = String(text || '').toLowerCase();
  if (query.includes('compare')) return 'compare';
  if (query.includes('conference') || query.includes('fintech')) return 'conferences';
  if (query.includes('cyber')) return 'cyber';
  if (query.includes('stripe') || query.includes('similar')) return 'similar';
  if (query.includes('healthcare') || query.includes('health')) return 'healthcare';
  if (query.includes('saas') || query.includes('new york')) return 'saas';
  if (query.includes('hvac') || query.includes('california')) return 'hvac';
  return 'default';
}

/**
 * @typedef {object} MockChatReply
 * @property {string} status
 * @property {string} message
 * @property {string} [artifact_id]
 * @property {object} [artifact]
 * @property {object[]} [candidates]
 * @property {{ key: string, label: string, prompt: string }[]} [suggestions]
 */

/**
 * @returns {MockChatReply}
 */
export function buildMockChatReply(prompt, turnCount = 0) {
  const kind = classifyMockChatPrompt(prompt);
  const suggestions = [
    { key: 'narrow', label: 'Narrow to 50–200 employees', prompt: 'Narrow this list to companies with 50–200 employees.' },
    { key: 'find_similar', label: 'Find similar companies', prompt: 'Find similar companies to this list.' },
  ];

  if (kind === 'hvac') {
    return {
      status: 'completed',
      message: 'Found 4 California HVAC operators. Previewing a saved target list you can refine.',
      ...HVAC_ARTIFACT,
      suggestions,
    };
  }
  if (kind === 'saas') {
    return {
      status: 'completed',
      message: 'Found 3 New York SaaS companies under 200 employees.',
      ...SAAS_ARTIFACT,
      suggestions,
    };
  }
  if (kind === 'cyber' || kind === 'similar') {
    const artifact = kind === 'similar'
      ? artifactPayload('mock-artifact-similar', 'Companies similar to Stripe', SAAS_CANDIDATES)
      : CYBER_ARTIFACT;
    return {
      status: 'completed',
      message: kind === 'similar'
        ? 'Here are software companies with a similar payments / B2B profile to Stripe.'
        : 'Found 3 cybersecurity companies with 50–200 employees.',
      ...artifact,
      suggestions,
    };
  }
  if (kind === 'healthcare') {
    return {
      status: 'completed',
      message: [
        'Fast-growing healthcare operators are concentrating in outpatient, behavioral, and home-health models.',
        '',
        'Signals I would track:',
        '• Clinic-count growth above 20% year over year',
        '• Payer mix shifting toward value-based contracts',
        '• Hiring velocity in clinical operations and revenue cycle',
        '',
        'Reference reading: https://www.cms.gov and https://www.beckershospitalreview.com',
      ].join('\n'),
      suggestions,
    };
  }
  if (kind === 'conferences') {
    return {
      status: 'completed',
      message: [
        'A few upcoming fintech events:',
        '• Money20/20 — https://www.money2020.com',
        '• FinTech Meetup — https://fintechmeetup.com',
        '• Sibos — https://www.sibos.com',
      ].join('\n'),
    };
  }
  if (kind === 'compare') {
    return {
      status: 'completed',
      message: [
        'Quick comparison of the current New York SaaS set:',
        '',
        'Northline Analytics',
        '• Focus: revenue operations',
        '• Scale: 118 employees / ~$22M revenue',
        '• Fit: strongest mid-market sales motion',
        '',
        'Kiteboard',
        '• Focus: collaborative planning',
        '• Scale: 74 employees / ~$11M revenue',
        '• Fit: earlier-stage, product-led growth',
        '',
        'Harbor Ledger',
        '• Focus: billing and collections',
        '• Scale: 160 employees / ~$28M revenue',
        '• Fit: closest to payments adjacency',
      ].join('\n'),
      suggestions,
    };
  }

  const defaults = [
    {
      status: 'completed',
      message: 'Got it. I can search companies, compare a shortlist, or look up nearby market events — what should we start with?',
    },
    {
      status: 'completed',
      message: [
        'I can work that as an AlphaSearch research pass. Typical next steps:',
        '• Build a company universe from industry, geography, and size',
        '• Rank operators by growth, headcount, and revenue signals',
        '• Compare 3–5 names and export a working list',
        '',
        'Try a prompt like “Find HVAC companies in California” to see a result table.',
      ].join('\n'),
    },
    {
      status: 'completed',
      message: 'Short answer: yes — I can run that as a company search. Add a geography or employee range if you want a tighter list.',
    },
  ];
  return defaults[turnCount % defaults.length];
}

function waitForMockReply() {
  if (process.env.NODE_ENV === 'test') return Promise.resolve();
  return new Promise((resolve) => {
    setTimeout(resolve, 850);
  });
}

export function listMockChatSessions(limit = 30) {
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 30;
  return {
    status: 200,
    data: {
      sessions: mockChatSessionStore.slice(0, safeLimit).map(sessionSummary),
    },
  };
}

export function getMockChatSession(sessionId) {
  const session = mockChatSessionStore.find((item) => item.session_id === sessionId);
  if (!session) {
    return {
      status: 404,
      data: { message: 'Chat session not found.' },
    };
  }

  return {
    status: 200,
    data: {
      session_id: session.session_id,
      status: session.status,
      workspace_id: session.workspace_id,
      messages: clone(session.messages || []),
    },
  };
}

export function createMockChatSession() {
  mockSessionCounter += 1;
  const now = new Date().toISOString();
  const session = {
    session_id: `mock-chat-new-${mockSessionCounter}`,
    status: 'active',
    workspace_id: null,
    title: 'New chat',
    last_message_preview: '',
    turn_count: 0,
    created_at: now,
    updated_at: now,
    last_artifact_id: null,
    messages: [],
  };
  mockChatSessionStore = [session, ...mockChatSessionStore];
  return {
    status: 201,
    data: {
      session_id: session.session_id,
      status: session.status,
      workspace_id: session.workspace_id,
    },
  };
}

/**
 * @param {string} sessionId
 * @param {{ title?: string, lastMessagePreview?: string }} input
 */
export function touchMockChatSession(sessionId, { title, lastMessagePreview } = {}) {
  const index = mockChatSessionStore.findIndex((item) => item.session_id === sessionId);
  if (index === -1) return;
  const current = mockChatSessionStore[index];
  const next = {
    ...current,
    title: title || current.title,
    last_message_preview: lastMessagePreview ?? current.last_message_preview,
    updated_at: new Date().toISOString(),
    turn_count: (current.turn_count || 0) + 1,
  };
  mockChatSessionStore = [next, ...mockChatSessionStore.filter((item) => item.session_id !== sessionId)];
}

export async function sendMockChatMessage(sessionId, content) {
  const index = mockChatSessionStore.findIndex((item) => item.session_id === sessionId);
  if (index === -1) {
    return {
      status: 404,
      data: { message: 'Chat session not found.' },
    };
  }

  await waitForMockReply();

  const session = mockChatSessionStore[index];
  const now = new Date().toISOString();
  const trimmed = String(content || '').trim();
  const userMessage = historyMessage({
    id: `${sessionId}-user-${Date.now()}`,
    role: 'user',
    content: trimmed,
    createdAt: now,
  });
  const reply = buildMockChatReply(trimmed, session.turn_count || 0);
  const assistantMessage = historyMessage({
    id: `${sessionId}-assistant-${Date.now()}`,
    role: 'assistant',
    content: reply.message,
    createdAt: now,
    extra: {
      artifact_id: reply.artifact_id,
      artifact: reply.artifact,
      candidates: reply.candidates,
      suggestions: reply.suggestions,
    },
  });
  const title = session.title && session.title !== 'New chat'
    ? session.title
    : trimmed.slice(0, 72) || 'New chat';
  const next = {
    ...session,
    title,
    last_message_preview: trimmed,
    updated_at: now,
    turn_count: (session.turn_count || 0) + 1,
    last_artifact_id: reply.artifact_id || session.last_artifact_id || null,
    messages: [...(session.messages || []), userMessage, assistantMessage],
  };
  mockChatSessionStore = [next, ...mockChatSessionStore.filter((item) => item.session_id !== sessionId)];

  return {
    status: 200,
    data: {
      status: reply.status,
      message: reply.message,
      message_id: assistantMessage.message_id,
      artifact_id: reply.artifact_id,
      artifact: reply.artifact,
      candidates: reply.candidates,
      suggestions: reply.suggestions || [],
    },
  };
}
